// This file has been modified from the original Blueprint file to match this project's linting rules and style and remove some unneeded code and update imports and refactor to a functional component.

/*
 * Copyright 2016 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import classNames from 'classnames';
import * as React from 'react';

import { type IconName } from '@blueprintjs/icons';

import { H4 } from '../Typography';
import * as Classes from './classes';
import { Icon } from './icon';

export type NonIdealStateProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> & {
  /** An action to resolve the non-ideal state which appears after `description`. */
  action?: React.JSX.Element;

  /**
   * A longer description of the non-ideal state.
   * A string or number value will be wrapped in a `<div>` to preserve margins.
   */
  description?: React.ReactNode;

  /** The name of a Blueprint icon or a JSX element (such as `<Spinner/>`) to render above the title. */
  icon?: IconName | React.ReactNode;

  /**
   * How large the icon visual should be.
   *
   * @default 48
   */
  iconSize?: number;

  /**
   * Whether the icon should use a muted style.
   *
   * @default true
   */
  iconMuted?: boolean;

  /** The title of the non-ideal state. */
  title?: React.ReactNode;
};

/**
 * Non-ideal state component.
 *
 * @see https://blueprintjs.com/docs/#core/components/non-ideal-state
 */
export function NonIdealState({
  action,
  className,
  iconMuted = true,
  iconSize = 48,
  icon,
  description,
  title,
  ...htmlProps
}: NonIdealStateProps) {
  const maybeRenderVisual = () => {
    if (icon == null) {
      return undefined;
    } else {
      return (
        <div
          className={Classes.NON_IDEAL_STATE_VISUAL}
          style={{ fontSize: `${iconSize}px`, lineHeight: `${iconSize}px` }}
        >
          {typeof icon === 'string' ? (
            <Icon
              className={classNames({ [Classes.ICON_MUTED]: iconMuted })}
              icon={icon as IconName}
              size={iconSize}
              aria-hidden={true}
              tabIndex={-1}
            />
          ) : (
            icon
          )}
        </div>
      );
    }
  };

  const maybeRenderText = () => {
    if (title == null && description == null) {
      return undefined;
    } else {
      return (
        <div className={Classes.NON_IDEAL_STATE_TEXT}>
          {title && <H4>{title}</H4>}
          {description && <div>{description}</div>}
        </div>
      );
    }
  };

  return (
    <div className={classNames(Classes.NON_IDEAL_STATE, className)} {...htmlProps}>
      {maybeRenderVisual()}
      {maybeRenderText()}
      {action}
    </div>
  );
}

NonIdealState.displayName = 'NonIdealState';
